import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MessageBar, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { useQueryClient } from '@tanstack/react-query';

import {
  TenantDetail,
  createTenantApplication,
  removeTenantApplicationPreviousKey,
  rotateTenantApplication,
} from '../api/tenantRegistry';
import GrantPermissionsButton from '../components/tenant/GrantPermissionsButton';
import { useEnvironment } from '../context/EnvironmentContext';
import { errorText } from '../lib/errorText';
import {
  CONNECT_CALLBACK_PATH,
  StashedSignIn,
  TenantConnectAction,
  describeSignInError,
  peekSignIn,
  takeSignIn,
} from '../lib/tenantConnectRedirect';
import { TEXT } from '../theme/tokens';

// R-FD-055 / FD-566 — where Microsoft returns the "Sign in & create" redirect.
//
// ORDER IS THE DESIGN:
//   1. Restore the environment the sign-in was started in. Microsoft returns to a
//      bare path with no `?env=`, and the active environment decides which Manager
//      receives the code. Redeeming against the default would act through the wrong
//      deployment. The stash is only PEEKED here, so the remount an environment
//      switch causes loses nothing.
//   2. TAKE the stash (read + delete) BEFORE the redemption call. Single use: a second
//      mount, a refresh, or a back-button cannot redeem the same code twice.
//   3. Strip the code from the address bar the moment it is read. It is spent either
//      way, but a URL that carries one is a URL someone pastes into a ticket.
//
// A return this tab did not start — no stash, an expired one, or a state that does
// not match — is refused without a server call. That is the CSRF property `state`
// exists for, and it is also the honest answer: there is nothing here to finish.

type Outcome =
  | { kind: 'working' }
  | { kind: 'done'; action: TenantConnectAction; tenant: TenantDetail }
  | { kind: 'not-ours' }
  | { kind: 'microsoft-error'; message: string; tenantId: string | null }
  | { kind: 'failed'; error: unknown; tenantId: string };

const DONE_COPY: Record<TenantConnectAction, string> = {
  create: 'Application created',
  rotate: 'Certificate rotated',
  'remove-previous-key': 'Superseded key removed',
};

export default function TenantConnectCallbackPage() {
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { environmentId, environments, setEnvironmentId, isSingleEnvironment } = useEnvironment();

  // Captured once: the URL is rewritten below, and these must not change under us.
  const [captured] = useState(() => ({
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
    errorDescription: params.get('error_description'),
  }));
  const [peeked] = useState<StashedSignIn | null>(() => peekSignIn(captured.state));
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'working' });
  const started = useRef(false);

  const wantEnv = peeked?.environmentId;
  const envKnown = wantEnv ? environments.some((e) => e.id === wantEnv) : false;
  const envReady = !wantEnv || !envKnown || wantEnv === environmentId;

  // Step 1 — put the right environment in place first.
  useEffect(() => {
    if (wantEnv && envKnown && wantEnv !== environmentId) setEnvironmentId(wantEnv);
  }, [wantEnv, envKnown, environmentId, setEnvironmentId]);

  // Steps 2 and 3 — take, strip, redeem. Once.
  useEffect(() => {
    if (!envReady || started.current) return;
    started.current = true;

    const stash = takeSignIn(captured.state);
    // Through the router, not history.replaceState: the router would otherwise keep
    // the old query and the environment provider could write the code back.
    navigate(
      isSingleEnvironment ? CONNECT_CALLBACK_PATH : `${CONNECT_CALLBACK_PATH}?env=${encodeURIComponent(environmentId)}`,
      { replace: true },
    );

    if (captured.error) {
      setOutcome({
        kind: 'microsoft-error',
        message: describeSignInError(captured.error, captured.errorDescription),
        tenantId: stash?.tenantId ?? null,
      });
      return;
    }
    if (!stash || !captured.code || (wantEnv && !envKnown)) {
      setOutcome({ kind: 'not-ours' });
      return;
    }

    const credential = {
      authorizationCode: captured.code,
      codeVerifier: stash.codeVerifier,
      redirectUri: stash.redirectUri,
    };
    void (async () => {
      try {
        const result =
          stash.action === 'create'
            ? await createTenantApplication(stash.tenantId, credential)
            : stash.action === 'rotate'
              ? await rotateTenantApplication(stash.tenantId, credential)
              : await removeTenantApplicationPreviousKey(stash.tenantId, credential);
        queryClient.setQueryData(['tenant', stash.tenantId], result.tenant);
        queryClient.invalidateQueries({ queryKey: ['tenants'] });
        setOutcome({ kind: 'done', action: stash.action, tenant: result.tenant });
      } catch (err) {
        setOutcome({ kind: 'failed', error: err, tenantId: stash.tenantId });
      }
    })();
  }, [envReady, captured, wantEnv, envKnown, environmentId, queryClient, navigate, isSingleEnvironment]);

  return (
    <div className="flex max-w-[640px] flex-col gap-6">
      <h1 className="font-display text-3xl tracking-tight">Connecting the tenant</h1>
      <CallbackBody outcome={outcome} />
    </div>
  );
}

function CallbackBody({ outcome }: { outcome: Outcome }) {
  if (outcome.kind === 'working') {
    return (
      <div className="flex items-center gap-3">
        <Spinner size="tiny" />
        <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          Finishing the sign-in and working in the tenant. This takes a few seconds.
        </span>
      </div>
    );
  }

  if (outcome.kind === 'done') {
    const t = outcome.tenant;
    const name = t.tenantDomain ?? t.displayName ?? t.tenantId;
    return (
      <div className="flex flex-col gap-4">
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>{DONE_COPY[outcome.action]}</MessageBarTitle>
            {outcome.action === 'create'
              ? `${name} now has its own AirBridge application. It holds no permissions until they are granted.`
              : `Done in ${name}.`}
          </MessageBarBody>
        </MessageBar>
        {outcome.action === 'create' ? (
          <div className="flex flex-col gap-2">
            <GrantPermissionsButton tenant={t} />
            <p className="m-0 font-display text-xs" style={{ color: TEXT.secondary }}>
              Next: a Global Administrator or Privileged Role Administrator of this tenant approves
              the application&rsquo;s permissions. You come straight back here.
            </p>
          </div>
        ) : null}
        <Link to={`/tenants/${encodeURIComponent(t.tenantId)}/overview`}>Open the tenant</Link>
      </div>
    );
  }

  if (outcome.kind === 'not-ours') {
    return (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>Nothing to finish here</MessageBarTitle>
          This sign-in was not started in this browser tab, or it expired before it came back.
          Nothing was created. Start it again from the tenant&rsquo;s Application tab.{' '}
          <Link to="/tenants">Go to tenants</Link>
        </MessageBarBody>
      </MessageBar>
    );
  }

  const tenantLink = outcome.tenantId ? (
    <Link to={`/tenants/${encodeURIComponent(outcome.tenantId)}/application`}>Back to the tenant</Link>
  ) : (
    <Link to="/tenants">Go to tenants</Link>
  );

  return (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>The sign-in did not complete</MessageBarTitle>
        {outcome.kind === 'microsoft-error' ? outcome.message : errorText(outcome.error)}{' '}
        {tenantLink}
      </MessageBarBody>
    </MessageBar>
  );
}
